import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { getService } from "./service";

const initialState = {
  trainingCourses: [],
  loading: false,
  error: null,
  total: 0,
};

const FETCH_REQUEST = "TRAINING_COURSE_FETCH_REQUEST";
const FETCH_SUCCESS = "TRAINING_COURSE_FETCH_SUCCESS";
const FETCH_FAILURE = "TRAINING_COURSE_FETCH_FAILURE";

const CREATE_REQUEST = "TRAINING_COURSE_CREATE_REQUEST";
const CREATE_SUCCESS = "TRAINING_COURSE_CREATE_SUCCESS";
const CREATE_FAILURE = "TRAINING_COURSE_CREATE_FAILURE";

const UPDATE_REQUEST = "TRAINING_COURSE_UPDATE_REQUEST";
const UPDATE_SUCCESS = "TRAINING_COURSE_UPDATE_SUCCESS";
const UPDATE_FAILURE = "TRAINING_COURSE_UPDATE_FAILURE";

const DELETE_REQUEST = "TRAINING_COURSE_DELETE_REQUEST";
const DELETE_SUCCESS = "TRAINING_COURSE_DELETE_SUCCESS";
const DELETE_FAILURE = "TRAINING_COURSE_DELETE_FAILURE";

export const getTrainingCourses =
  (
    page = 0,
    pageSize = 20,
    sortField = "id",
    sortDirection = "asc",
    filters = {},
    userAgency
  ) =>
  async (dispatch) => {
    const service = getService(serviceNames.trainingCourse);
    const query = {
      $limit: pageSize,
      $skip: page * pageSize,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
      // lastName: { $like: '%Sharma%'}, // for searching in the fields
      isDeleted: false,
      agencyId: userAgency,
    };

    const textFields = [
      "courseName",
      "instructor",
      "category",
      "hours",
      "courseNumber",
    ];

    textFields.forEach((field) => {
      const value = filters[field];
      if (value) {
        query[field] = filters.exactMatch ? value : { $like: `%${value}%` };
      }
    });

    const getQueryValue = (input) => {
      if (Array.isArray(input)) {
        const values = input.map((i) => i.value).filter(Boolean);
        return values.length > 0 ? { $in: values } : undefined;
      } else if (input?.value) {
        return input.value;
      }
      return undefined;
    };

    const dropdownFields = ["location", "category"];

    dropdownFields.forEach((field) => {
      const value = getQueryValue(filters[field]);
      if (value !== undefined) {
        query[field] = value;
      }
    });

    if (Array.isArray(filters.tagTypes) && filters.tagTypes.length > 0) {
      query.tagTypes = filters.tagTypes.map((t) => t.value);
    }

    if (filters.filterField?.value && filters.filterValue) {
      const fieldMap = {};
      const field =
        fieldMap[filters.filterField.value] || filters.filterField.value;

      query[field] = filters.exactMatch
        ? filters.filterValue
        : { $like: `%${filters.filterValue}%` };
    }

    if (filters.courseBeginDate) {
      query.courseBeginDate = {
        ...(query.courseBeginDate || {}),
        $gte: filters.courseBeginDate,
      };
    }

    if (filters.courseEndDate) {
      query.courseEndDate = {
        ...(query.courseEndDate || {}),
        $lte: filters.courseEndDate,
      };
    }

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_REQUEST,
        success: FETCH_SUCCESS,
        failure: FETCH_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (res) => {
        return {
          trainingCourses: res.data,
          total: res.total,
        };
      },
      errorMessagePrefix: "Failed to fetch training courses",
    });
  };

export const createTrainingCourse =
  (trainingCourseData) => async (dispatch) => {
    const service = getService(serviceNames.trainingCourse);

    try {
      if (trainingCourseData && trainingCourseData.courseNumber) {
        const result = await service.find({
          query: {
            courseNumber: trainingCourseData.courseNumber,
          },
        });

        if (result.total > 0) {
          return { duplicate: true };
        }
      }

      return await asyncThunkWrapper({
        dispatch,
        types: {
          request: CREATE_REQUEST,
          success: CREATE_SUCCESS,
          failure: CREATE_FAILURE,
        },
        apiCall: () => service.create(trainingCourseData),
        successMessage: "Training Course created successfully",
        errorMessagePrefix: "Failed to create training course",
      });
    } catch (err) {
      dispatch({
        type: CREATE_FAILURE,
        payload: err.message,
      });
      return null;
    }
  };

export const updateTrainingCourse =
  (id, trainingCourseData) => async (dispatch) => {
    const service = getService(serviceNames.trainingCourse);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: UPDATE_REQUEST,
        success: UPDATE_SUCCESS,
        failure: UPDATE_FAILURE,
      },
      apiCall: () => service.patch(id, trainingCourseData),
      successMessage: "Training Course updated successfully",
      errorMessagePrefix: "Failed to update training course",
    });
  };

export const deleteTrainingCourse = (id, userId) => async (dispatch) => {
  const service = getService(serviceNames.trainingCourse);
  const patchData = {
    isDeleted: true,
    deletedBy: userId,
    deletedAt: new Date().toISOString(),
  };

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: DELETE_REQUEST,
      success: DELETE_SUCCESS,
      failure: DELETE_FAILURE,
    },
    apiCall: () => service.patch(id, patchData),
    transformResult: () => id,
    successMessage: "Training Course deleted successfully",
    errorMessagePrefix: "Failed to delete training course",
  });
};

export default function trainingCourseReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_REQUEST:
    case CREATE_REQUEST:
    case UPDATE_REQUEST:
    case DELETE_REQUEST:
      return { ...state, loading: true, error: null };
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingCourses: action.payload.trainingCourses,
        total: action.payload.total,
      };
    case CREATE_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingCourses: [...state.trainingCourses, action.payload],
        total: state.total + 1,
      };
    case UPDATE_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingCourses: state.trainingCourses.map((tc) =>
          tc.id === action.payload.id ? action.payload : tc
        ),
      };
    case DELETE_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingCourses: state.trainingCourses.filter(
          (tc) => tc.id !== action.payload
        ),
        total: state.total - 1,
      };
    case FETCH_FAILURE:
    case CREATE_FAILURE:
    case UPDATE_FAILURE:
    case DELETE_FAILURE:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}
